// PROTOTYPE (hero shapes): Costellazione's count, painted on its canvas over
// the clients and AVRAH's ball (DotCanvas.jsx, DiscTransitions.jsx). It is
// set in Poppins, flat, each figure as wide as a 0 so it never shifts as it
// climbs.

// `fallback` sets it as the page's text would be (src/index.css), should
// Poppins never load.
export const COUNT_FONT = { family: '"Poppins"', fallback: '"Poppins Fallback", sans-serif', weight: 600, tracking: -0.04 };

// How many stops the count's own ink takes, top to foot of its digits.
const STOPS = 4;

// `text` laid out flat in `ctx`, `size` its font size: its font, each
// character and how far its middle is from the digits' left, their width
// and how high they stand. Each figure is as wide as a 0, and each
// character is set in the middle of its width.
function layOut(ctx, text, size) {
  const font = `${COUNT_FONT.weight} ${size}px ${COUNT_FONT.family}, ${COUNT_FONT.fallback}`;
  ctx.font = font;
  const figure = ctx.measureText("0");
  const tracking = COUNT_FONT.tracking * size;
  let at = 0;
  const chars = [...text].map((ch) => {
    const own = /\d/.test(ch) ? figure.width : ctx.measureText(ch).width;
    const middle = at + own / 2;
    at += own + tracking;
    return { ch, middle };
  });
  return { font, chars, width: Math.max(0, at - tracking), height: figure.actualBoundingBoxAscent };
}

// The count `text`, `size` its font size in CSS pixels, its digits' middle
// at (`x`, `y`). It is in its own `ink` ([r, g, b] at a point
// of a digit, `v` down from its middle, `height` the digits' height); where
// the ball `ball` ({ x, y, r }, on the canvas) lies under it, in `printed`,
// a CSS colour, flat, so the ball's edge wipes across it as the ball grows,
// as smooth as the ball's own edge.
export function paintCount(ctx, { text, size, x, y, ink, ball = null, printed = null }) {
  if (ball !== null && !printed) throw new Error("paintCount: the count on the ball needs an ink to be set in there");
  ctx.save();
  const { chars, width, height } = layOut(ctx, text, size);
  const own = ctx.createLinearGradient(0, -height / 2, 0, height / 2);
  for (let k = 0; k <= STOPS; k++) own.addColorStop(k / STOPS, cssOf(ink(height * (k / STOPS - 0.5), height)));
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  // Set from the digits' middle, where their ink is laid out from.
  const set = (fill) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = fill;
    chars.forEach(({ ch, middle }) => ctx.fillText(ch, middle - width / 2, height / 2));
    ctx.restore();
  };
  if (ball === null || !(ball.r > 0)) set(own);
  else {
    // The whole canvas, in the units it is drawn in, less the ball.
    const { a, d } = ctx.getTransform();
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, ctx.canvas.width / a, ctx.canvas.height / d);
    ctx.arc(ball.x, ball.y, ball.r, 0, 2 * Math.PI, true);
    ctx.clip("evenodd");
    set(own);
    ctx.restore();
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.r, 0, 2 * Math.PI);
    ctx.clip();
    set(printed);
  }
  ctx.restore();
}

const mix = (a, b, t) => a.map((channel, c) => channel + (b[c] - channel) * t);
const clamp01 = (value) => Math.min(1, Math.max(0, value));
const cssOf = (rgb) => {
  const [r, g, b] = rgb.map(Math.round);
  return `rgb(${r}, ${g}, ${b})`;
};

// A token's colour, as [r, g, b]: the page's are all #rrggbb.
export function rgbOf(token) {
  const hex = /^#([0-9a-f]{6})$/i.exec(token.trim());
  if (!hex) throw new Error(`paintCount: "${token}" is not a colour as #rrggbb`);
  return [0, 2, 4].map((at) => parseInt(hex[1].slice(at, at + 2), 16));
}

// Gold ink: the page's gold (--gold) as if lit from above, a little lighter
// at the digits' top and deeper at their foot, no more.
const GOLD_TOP = [233, 180, 55];
const GOLD_FOOT = [201, 143, 13];
function goldInk(v, height) {
  return mix(GOLD_TOP, GOLD_FOOT, clamp01(v / height + 0.5));
}

// The count's own ink, at a point of its digits `v` down from their middle,
// `height` their height: `white`, `lit` of the way to gold.
export function countInk({ white, lit }) {
  return (v, height) => (lit > 0 ? mix(white, goldInk(v, height), lit) : white);
}
